import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useLocale, useTranslations } from "use-intl";

import { Alert, AlertDescription, AlertTitle } from "@lawline/ui/components/alert";
import { Button } from "@lawline/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader } from "@lawline/ui/components/card";
import { Skeleton } from "@lawline/ui/components/skeleton";

import { useAuth } from "@platform/auth/context";
import { CopyButton } from "@platform/components/ui/copy-button";
import { ExternalLink } from "@platform/components/ui/external-link";
import { docsLink } from "@platform/core/docs";
import { mcpServerQueryOptions } from "./ai-apps.queries";
import { ConnectFlow } from "./components/connect-flow";
import { ConnectSteps } from "./components/connect-steps";
import { ConnectedApps } from "./components/connected-apps";

export function AiAppsPage() {
	const t = useTranslations("aiApps");
	const locale = useLocale();
	const { can, role } = useAuth();
	const server = useQuery(mcpServerQueryOptions);
	const url = server.data;

	return (
		<section className="mx-auto mt-2 flex w-full max-w-3xl flex-col gap-6">
			<div className="flex flex-col gap-2">
				<h1 className="text-2xl font-semibold">{t("title")}</h1>
				<p className="text-muted-foreground text-sm">{t("intro")}</p>
			</div>

			{/* The route loads the membership first, so a null role means no organisation is active.
			    The server binds a connection to the user who approves it and lets only a role that
			    creates keys approve, so nobody can connect an app on a member's behalf. */}
			{role === null ? (
				<Alert>
					<AlertTitle>{t("access.noOrganizationTitle")}</AlertTitle>
					<AlertDescription>
						<p>
							{t.rich("access.noOrganization", {
								link: (chunks) => (
									<Link
										to="/tenant"
										search={{ create: true }}
										className="text-foreground underline underline-offset-2"
									>
										{chunks}
									</Link>
								),
							})}
						</p>
					</AlertDescription>
				</Alert>
			) : !can({ apiKey: ["create"] }) ? (
				<Alert>
					<AlertTitle>{t("access.memberTitle")}</AlertTitle>
					<AlertDescription>{t("access.member")}</AlertDescription>
				</Alert>
			) : null}

			{server.isPending ? (
				<div role="status" className="flex flex-col gap-2">
					<span className="sr-only">{t("server.loading")}</span>
					<Skeleton className="h-24 w-full" />
				</div>
			) : server.isError ? (
				<Alert variant="destructive">
					<AlertTitle>{t("server.failedTitle")}</AlertTitle>
					<AlertDescription className="flex flex-col items-start gap-2">
						<span>{t("server.failed")}</span>
						<Button variant="outline" size="sm" onClick={() => void server.refetch()}>
							{t("server.retry")}
						</Button>
					</AlertDescription>
				</Alert>
			) : url ? (
				<Card>
					<CardHeader>
						<h2 className="text-base font-medium">{t("server.title")}</h2>
						<CardDescription>{t("server.description")}</CardDescription>
					</CardHeader>
					<CardContent className="flex flex-wrap items-start justify-between gap-2">
						<code className="bg-muted min-w-0 flex-1 basis-60 rounded-md border px-3 py-1.5 font-mono text-sm wrap-anywhere">
							{url}
						</code>
						<CopyButton value={url} label={t("server.copy")} />
					</CardContent>
				</Card>
			) : (
				<Alert>
					<AlertTitle>{t("server.unavailableTitle")}</AlertTitle>
					<AlertDescription>
						<p>
							{t.rich("server.unavailable", {
								docs: (chunks) => (
									<ExternalLink href={docsLink("mcp", locale)}>{chunks}</ExternalLink>
								),
							})}
						</p>
					</AlertDescription>
				</Alert>
			)}

			{url ? (
				<>
					<ConnectSteps url={url} />
					<ConnectFlow />
				</>
			) : null}

			{/* Revoking stays possible while the URL cannot be read. Where sign-in is off, no
			    connection works, so the list would only mislead. */}
			{url !== null && can({ apiKey: ["read"] }) ? <ConnectedApps /> : null}
		</section>
	);
}
